import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import PropTypes from 'prop-types';
import React, {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import TextareaAutosize from 'react-textarea-autosize';
import { FormGroup, Input, Label } from 'reactstrap';
import RichTextViewer from './RichTextViewer';

export const YES_NO_OPTIONS = (formatMessage: IntlShape['formatMessage']) => [
  {
    id: 'true',
    name: formatMessage({
      id: 'app.views.widgets.inputs.validated_input.yes',
      defaultMessage: 'Yes',
    }),
  },
  {
    id: 'false',
    name: formatMessage({
      id: 'app.views.widgets.inputs.validated_input.no',
      defaultMessage: 'No',
    }),
  },
];

export const toYesNoValue = (value: unknown): string | undefined => {
  if (value == undefined) {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (value === 'true' || value === 'false') {
    return value;
  }
  return undefined;
};

export const toBoolean = (value: unknown) => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

const MultipleChoiceInput: FC<Props> = (props) => {
  const { formatMessage } = useIntl();
  const [validationMessage, setValidationMessage] = useState(null);
  const optionsAsObject =
    props.options?.length > 0 && typeof props.options[0] === 'string'
      ? props.options.map((s) => ({
          id: s,
          name: s,
        }))
      : props.options;

  const [customInputIsFocused, setCustomInputIsFocused] = useState(false);
  const [customOptionValue, setCustomOptionValue] = useState(
    props.allowCustom &&
      // @ts-expect-error
      props.value?.length > 0 &&
      optionsAsObject &&
      // @ts-expect-error
      optionsAsObject.findIndex((o) => o.id === props.value) === -1
      ? props.value
      : ''
  );
  const ref = useRef();
  const propsOnChange = props.onChange;
  // @ts-expect-error
  const propsOnValidChange = props.onValidChange;
  const propsRequired = props.required;
  const incomingValue = props.value;

  useEffect(() => {
    if (propsRequired) {
      const isValid =
        typeof incomingValue !== 'undefined' && incomingValue !== null;
      setValidationMessage(
        // @ts-expect-error
        isValid
          ? null
          : formatMessage({
              id: 'app.widgets.inputs.multiple_choice_input.please_select_an_answer',
              defaultMessage: 'Please select an answer.',
            })
      );
    } else {
      setValidationMessage(null);
    }
  }, [incomingValue, propsRequired, formatMessage]);

  useEffect(() => {
    if (propsOnValidChange) {
      propsOnValidChange(validationMessage);
    }
  }, [validationMessage, propsOnValidChange]);

  useEffect(() => {
    if (
      props.allowCustom &&
      // @ts-expect-error
      props.value?.length > 0 &&
      optionsAsObject &&
      // @ts-expect-error
      optionsAsObject.findIndex((o) => o.id === props.value) === -1
    ) {
      setCustomOptionValue(props.value);
    }
  }, [props.allowCustom, optionsAsObject, props.value]);

  useEffect(() => {
    if (customInputIsFocused && ref?.current) {
      // @ts-expect-error
      ref.current.focus();
    }
  }, [customInputIsFocused]);

  const onFocusCustomValue = useCallback(() => {
    // populate value to ensure radio button is selected
    // @ts-expect-error
    propsOnChange(customOptionValue);
  }, [customOptionValue, propsOnChange]);

  const onBlurCustomValue = useCallback(() => {
    // populate value to ensure radio button is selected
    setCustomInputIsFocused(false);
  }, []);

  const onChange = useCallback(
    (e) => {
      // @ts-expect-error
      propsOnChange(e.target.value);
    },
    [propsOnChange]
  );

  const onChangeCustomValue = useCallback(
    (e) => {
      setCustomOptionValue(e.target.value);
      // @ts-expect-error
      propsOnChange(e.target.value);
    },
    [propsOnChange]
  );

  const onCustomValueRadioClicked = useCallback(() => {
    setCustomInputIsFocused(true);
    onChange({
      target: {
        value: customOptionValue,
      },
    });
  }, [onChange, customOptionValue]);

  const customItemIsSelected =
    props.value === customOptionValue || customInputIsFocused;

  // NOTE: "RichTextViewer" is used for customOptionLabel below
  // so we can render links from org settings (e.g. promo packet
  // custom urls for promo packet templates)
  return (
    <>
      {props.title && <Label className="mb-2 fw-bold">{props.title}</Label>}
      <FormGroup tag="fieldset" name={props.name} required={props.required}>
        {optionsAsObject?.map((option, optionIndex) => {
          // having an ID with a period is invalid
          // @ts-expect-error
          const popoverId = ('mc-' + props.name + '-' + option.id).replaceAll(
            '.',
            '_'
          );

          return (
            <Fragment key={optionIndex}>
              <FormGroup check className="mb-2">
                <Label check id={popoverId} role="button">
                  <Input
                    required={props.required}
                    role="button"
                    type="radio"
                    name={props.name}
                    // @ts-expect-error
                    value={option.id}
                    // @ts-expect-error
                    checked={props.value?.toString() === option.id?.toString()}
                    onChange={onChange}
                    disabled={props.disabled}
                  />{' '}
                  {/* @ts-expect-error */}
                  {option.name} {/* @ts-expect-error */}
                  {!option.popoverContent && option.description && (
                    <i
                      style={{
                        cursor: 'auto',
                      }}
                      id={popoverId + '-question'}
                      className={
                        'fe fe-help-circle text-primary small pl-3 pr-0'
                      }
                    />
                  )}
                </Label>
                {/* @ts-expect-error */}
                {option?.helperText && (
                  <div>
                    <small className="ps-0 pt-0 text-muted">
                      {/* @ts-expect-error */}
                      {option.helperText}
                    </small>
                  </div>
                )}
              </FormGroup>
              {/* @ts-expect-error */}

              {option.popoverContent && (
                <UncontrolledPopover trigger="hover" target={popoverId}>
                  {/* @ts-expect-error */}
                  {option.popoverContent}
                </UncontrolledPopover>
              )}
              {/* @ts-expect-error */}
              {!option.popoverContent && option.description && (
                <UncontrolledPopover trigger="hover" target={popoverId}>
                  {/* @ts-expect-error */}
                  {option.description}
                </UncontrolledPopover>
              )}
            </Fragment>
          );
        })}
        {props.allowCustom && (
          <FormGroup check>
            <Label check role="button" className="w-100">
              <Input
                role="button"
                type="radio"
                name={props.name}
                value={customOptionValue}
                checked={customItemIsSelected}
                onChange={onCustomValueRadioClicked}
                disabled={props.disabled}
              />{' '}
              {props.customOptionText ??
                formatMessage({
                  id: 'app.widgets.inputs.multiple_choice_input.custom',
                  defaultMessage: 'Custom',
                })}
              {customItemIsSelected && (
                <div className="mt-2">
                  <RichTextViewer
                    // @ts-expect-error
                    model={props.customOptionLabel}
                    expanded={true}
                  />
                </div>
              )}
              <TextareaAutosize
                // @ts-expect-error
                ref={ref}
                type="text"
                className={
                  'mt-2 form-control w-100' +
                  (customItemIsSelected ? '' : ' d-none')
                }
                placeholder={
                  props.customOptionPlaceholder ??
                  formatMessage({
                    id: 'app.widgets.inputs.multiple_choice_input.enter_a_custom_answer',
                    defaultMessage: 'Enter a custom answer',
                  })
                }
                onFocus={onFocusCustomValue}
                onBlur={onBlurCustomValue}
                onChange={onChangeCustomValue}
                // @ts-expect-error
                value={customOptionValue}
                // @ts-expect-error
                disabled={props.disabled}
              />
            </Label>
          </FormGroup>
        )}
      </FormGroup>
    </>
  );
};

const MultipleChoiceInput_propTypes = {
  required: PropTypes.bool,
  title: PropTypes.string,
  helperText: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  style: PropTypes.object,
  className: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.string])
  ).isRequired,
  allowCustom: PropTypes.bool,
  customOptionText: PropTypes.string,
  customOptionLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  customOptionPlaceholder: PropTypes.string,
  disabled: PropTypes.bool,
};

type Props = PropTypes.InferProps<typeof MultipleChoiceInput_propTypes>;

export default React.memo(MultipleChoiceInput);
